/**
 * RoleOverridePanel — Admin UI for tenant-specific role customization (DRR-258 Phase 4).
 *
 * Enables facility admins to:
 *   - View current role overrides for the tenant
 *   - Add/remove scopes for specific roles
 *   - Set effective date ranges for overrides
 *   - Deactivate overrides
 *
 * Security:
 *   - Requires role:override:write scope
 *   - Can only ADD scopes the admin already has (no privilege escalation)
 *   - All changes logged to Wasabi audit trail
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Stack,
  Paper,
  Group,
  Title,
  Text,
  Badge,
  Button,
  Modal,
  TextInput,
  MultiSelect,
  Textarea,
  Switch,
  Alert,
  Loader,
  Table,
  ActionIcon,
  Tooltip,
  Divider,
  Box,
  Card,
  ThemeIcon,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconShield,
  IconShieldCheck,
  IconShieldOff,
  IconAlertCircle,
  IconRefresh,
  IconCalendar,
} from '@tabler/icons-react';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';

const LIST_ROLE_OVERRIDES = gql`
  query ListRoleOverrides($limit: Int, $cursor: String) {
    listRoleOverrides(limit: $limit, cursor: $cursor) {
      items {
        overrideId
        baseRoleId
        name
        description
        addScopes
        removeScopes
        overrideViewLayer
        overrideWall
        tenantNum
        createdAt
        updatedAt
      }
      nextCursor
      hasMore
    }
  }
`;

const CREATE_ROLE_OVERRIDE = gql`
  mutation CreateRoleOverride($input: CreateRoleOverrideInput!) {
    createRoleOverride(input: $input) {
      overrideId
      baseRoleId
      name
      description
      addScopes
      removeScopes
      createdAt
    }
  }
`;

const UPDATE_ROLE_OVERRIDE = gql`
  mutation UpdateRoleOverride($input: UpdateRoleOverrideInput!) {
    updateRoleOverride(input: $input) {
      overrideId
      baseRoleId
      name
      description
      addScopes
      removeScopes
      updatedAt
    }
  }
`;

const DEACTIVATE_ROLE_OVERRIDE = gql`
  mutation DeactivateRoleOverride($overrideId: String!) {
    deactivateRoleOverride(overrideId: $overrideId)
  }
`;

interface RoleOverride {
  overrideId: string;
  baseRoleId: string;
  name?: string;
  description?: string;
  addScopes: string[];
  removeScopes: string[];
  overrideViewLayer?: string;
  overrideWall?: string;
  tenantNum: number;
  createdAt: number;
  updatedAt: number;
}

interface RoleOverridePanelProps {
  tenantId?: string;
  availableRoles?: Array<{ id: string; name: string }>;
  availableScopes?: string[];
  currentUserScopes?: string[];
}

export function RoleOverridePanel({
  tenantId,
  availableRoles = [],
  availableScopes = [],
  currentUserScopes = [],
}: RoleOverridePanelProps) {
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);
  const [editingOverride, setEditingOverride] = useState<RoleOverride | null>(null);

  const { data, loading, error, refetch } = useQuery(LIST_ROLE_OVERRIDES, {
    variables: { limit: 100 },
    fetchPolicy: 'cache-and-network',
  });

  const [createOverride, { loading: creating }] = useMutation(CREATE_ROLE_OVERRIDE, {
    onCompleted: () => {
      closeCreateModal();
      refetch();
    },
  });

  const [updateOverride, { loading: updating }] = useMutation(UPDATE_ROLE_OVERRIDE, {
    onCompleted: () => {
      setEditingOverride(null);
      refetch();
    },
  });

  const [deactivateOverride] = useMutation(DEACTIVATE_ROLE_OVERRIDE, {
    onCompleted: () => refetch(),
  });

  const form = useForm({
    initialValues: {
      baseRoleId: '',
      name: '',
      description: '',
      addScopes: [] as string[],
      removeScopes: [] as string[],
    },
  });

  const handleCreate = useCallback(async (values: typeof form.values) => {
    await createOverride({
      variables: {
        input: {
          baseRoleId: values.baseRoleId,
          name: values.name || undefined,
          description: values.description || undefined,
          addScopes: values.addScopes,
          removeScopes: values.removeScopes,
        },
      },
    });
    form.reset();
  }, [createOverride, form]);

  const handleUpdate = useCallback(async (values: typeof form.values) => {
    if (!editingOverride) return;
    await updateOverride({
      variables: {
        input: {
          overrideId: editingOverride.overrideId,
          name: values.name || undefined,
          description: values.description || undefined,
          addScopes: values.addScopes,
          removeScopes: values.removeScopes,
        },
      },
    });
    form.reset();
  }, [updateOverride, editingOverride, form]);

  const handleDeactivate = useCallback(async (overrideId: string) => {
    if (window.confirm('Deactivate this role override? The base role scopes will apply.')) {
      await deactivateOverride({ variables: { overrideId } });
    }
  }, [deactivateOverride]);

  const openEdit = useCallback((override: RoleOverride) => {
    setEditingOverride(override);
    form.setValues({
      baseRoleId: override.baseRoleId,
      name: override.name || '',
      description: override.description || '',
      addScopes: override.addScopes || [],
      removeScopes: override.removeScopes || [],
    });
  }, [form]);

  const overrides = data?.listRoleOverrides?.items || [];

  const scopeOptions = useMemo(() =>
    availableScopes.map((s) => ({ value: s, label: s })),
  [availableScopes]);

  const roleOptions = useMemo(() =>
    availableRoles.map((r) => ({ value: r.id, label: r.name })),
  [availableRoles]);

  if (loading && !data) {
    return (
      <Stack align="center" py="xl">
        <Loader size="lg" />
        <Text c="dimmed">Loading role overrides...</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="red" icon={<IconAlertCircle />}>
        Failed to load role overrides: {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={4}>Role Overrides</Title>
          <Text size="sm" c="dimmed">
            Customize role scopes for this tenant without modifying system roles
          </Text>
        </div>
        <Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="subtle"
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={openCreateModal}
          >
            Add Override
          </Button>
        </Group>
      </Group>

      <Alert color="blue" variant="light" icon={<IconShieldCheck size={16} />}>
        <Text size="sm">
          Role overrides allow tenant-specific scope customization. Changes are logged to the audit trail.
          You can only add scopes you already have (no privilege escalation).
        </Text>
      </Alert>

      {overrides.length === 0 ? (
        <Paper p="xl" withBorder>
          <Stack align="center" gap="md">
            <ThemeIcon size="xl" variant="light" color="gray">
              <IconShield size={24} />
            </ThemeIcon>
            <Text c="dimmed">No role overrides configured for this tenant</Text>
            <Button leftSection={<IconPlus size={16} />} onClick={openCreateModal}>
              Create First Override
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Base Role</Table.Th>
              <Table.Th>Added Scopes</Table.Th>
              <Table.Th>Removed Scopes</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Updated</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {overrides.map((override: RoleOverride) => (
              <Table.Tr key={override.overrideId}>
                <Table.Td>
                  <Badge variant="light">{override.baseRoleId}</Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {override.addScopes?.slice(0, 3).map((s) => (
                      <Badge key={s} size="xs" color="green" variant="dot">
                        {s}
                      </Badge>
                    ))}
                    {(override.addScopes?.length || 0) > 3 && (
                      <Text size="xs" c="dimmed">
                        +{override.addScopes.length - 3} more
                      </Text>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {override.removeScopes?.slice(0, 3).map((s) => (
                      <Badge key={s} size="xs" color="red" variant="dot">
                        {s}
                      </Badge>
                    ))}
                    {(override.removeScopes?.length || 0) > 3 && (
                      <Text size="xs" c="dimmed">
                        +{override.removeScopes.length - 3} more
                      </Text>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  {override.isActive ? (
                    <Badge color="green">Active</Badge>
                  ) : (
                    <Badge color="gray">Inactive</Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {override.updatedAt
                      ? new Date(override.updatedAt).toLocaleDateString()
                      : new Date(override.createdAt).toLocaleDateString()}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <Tooltip label="Edit">
                      <ActionIcon variant="subtle" onClick={() => openEdit(override)}>
                        <IconEdit size={16} />
                      </ActionIcon>
                    </Tooltip>
                    {override.isActive && (
                      <Tooltip label="Deactivate">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => handleDeactivate(override.overrideId)}
                        >
                          <IconShieldOff size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {/* Create Modal */}
      <Modal
        opened={createModalOpened}
        onClose={closeCreateModal}
        title="Create Role Override"
        size="lg"
      >
        <form onSubmit={form.onSubmit(handleCreate)}>
          <Stack gap="md">
            <TextInput
              label="Base Role"
              placeholder="e.g., 144g-ald"
              required
              {...form.getInputProps('baseRoleId')}
            />

            <MultiSelect
              label="Add Scopes"
              description="Scopes to add to this role for this tenant"
              placeholder="Select scopes to add"
              data={scopeOptions}
              searchable
              {...form.getInputProps('addScopes')}
            />

            <MultiSelect
              label="Remove Scopes"
              description="Scopes to remove from this role (use with caution)"
              placeholder="Select scopes to remove"
              data={scopeOptions}
              searchable
              {...form.getInputProps('removeScopes')}
            />

            <Group grow>
              <DatePickerInput
                label="Effective From"
                placeholder="Optional start date"
                clearable
                {...form.getInputProps('effectiveFrom')}
              />
              <DatePickerInput
                label="Effective Until"
                placeholder="Optional end date"
                clearable
                {...form.getInputProps('effectiveUntil')}
              />
            </Group>

            <Textarea
              label="Reason"
              placeholder="Why is this override needed?"
              {...form.getInputProps('reason')}
            />

            <Group justify="flex-end">
              <Button variant="subtle" onClick={closeCreateModal}>
                Cancel
              </Button>
              <Button type="submit" loading={creating}>
                Create Override
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        opened={!!editingOverride}
        onClose={() => setEditingOverride(null)}
        title={`Edit Override: ${editingOverride?.baseRoleId}`}
        size="lg"
      >
        <form onSubmit={form.onSubmit(handleUpdate)}>
          <Stack gap="md">
            <TextInput
              label="Base Role"
              value={editingOverride?.baseRoleId || ''}
              disabled
            />

            <MultiSelect
              label="Add Scopes"
              description="Scopes to add to this role for this tenant"
              placeholder="Select scopes to add"
              data={scopeOptions}
              searchable
              {...form.getInputProps('addScopes')}
            />

            <MultiSelect
              label="Remove Scopes"
              description="Scopes to remove from this role (use with caution)"
              placeholder="Select scopes to remove"
              data={scopeOptions}
              searchable
              {...form.getInputProps('removeScopes')}
            />

            <Group grow>
              <DatePickerInput
                label="Effective From"
                placeholder="Optional start date"
                clearable
                {...form.getInputProps('effectiveFrom')}
              />
              <DatePickerInput
                label="Effective Until"
                placeholder="Optional end date"
                clearable
                {...form.getInputProps('effectiveUntil')}
              />
            </Group>

            <Textarea
              label="Reason"
              placeholder="Why is this override needed?"
              {...form.getInputProps('reason')}
            />

            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setEditingOverride(null)}>
                Cancel
              </Button>
              <Button type="submit" loading={updating}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}

export default RoleOverridePanel;
